import { useKeyPressEvent } from "react-use";
import React, { ReactElement, ReactNode } from "react";
import styled from "styled-components/macro";
import { CloseIcon } from "./assets";
import { createPortal } from "react-dom";

type Position = "center" | "right" | "left";

type StyledRootProps = Omit<ModalProps, "onClose" | "className" | "title">;

const StyledRoot = styled.div<StyledRootProps>`
  position: fixed;
  display: flex;
  align-items: ${({ position }) =>
    position === "center" || !position
      ? "center"
      : position === "right"
      ? "end"
      : "center"};
  justify-content: ${({ position }) =>
    position === "center" || !position
      ? "center"
      : position === "right"
      ? "end"
      : "center"};
  backdrop-filter: ${(props) => (props.backgroundBlur ? "blur(0.5rem)" : "")};
  background: rgba(0, 0, 0, 0.2);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10100;

  @media screen and (max-width: 40rem) {
    position: absolute;
    align-items: start;
    z-index: 100000;
  }

  .modal {
    animation: ${({ animate, slideFromDirection: fromDirection }) =>
        animate
          ? fromDirection === "right"
            ? "slide-in-from-right"
            : fromDirection === "bottom"
            ? "short-slide-up"
            : "bouncy"
          : "scale-from-small"}
      0.2s ease-in;
    overflow: hidden;
    min-width: ${(props) => (props.minWidth ? props.minWidth : "40rem")};
    min-height: ${(props) => (props.minHeight ? props.minHeight : "auto")};
    width: ${(props) => (props.width ? props.width : "auto")};
    height: ${(props) => (props.height ? props.height : "auto")};
    max-height: ${(props) =>
      props.maxHeight ? props.maxHeight : "calc(100vh - 4rem)"};
    max-width: ${(props) => (props.maxWidth ? props.maxWidth : null)};
    background: white;
    border-radius: ${({ position }) => (position === "center" ? "0.3em" : "0")};
    box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    z-index: 10100;

    @media screen and (max-width: 40rem) {
      width: 100%;
      min-width: min-content;
      max-width: 90vw;
      margin-top: ${({ position }) => (position === "center" ? "1rem" : "0")};
      & > * {
        width: 100%;
      }
    }

    header {
      flex-grow: 0;
      flex-shrink: 0;
      padding: 1rem;
      border-bottom: 1px solid var(--border-gray);
      position: relative;
      display: flex;
      flex-direction: row;

      h3 {
        margin: 0;
        padding: 0;
        width: calc(100% - 2rem);
      }

      .close-icon {
        position: absolute;
        top: calc(50% - var(--icon-size) / 2);
        right: 20px;
      }
    }

    .body {
      overflow: auto;
      flex-grow: 1;
      padding: ${(props) => (props.padding ? props.padding : "1rem")};
      ${(props) =>
        props.backgroundColor ? `background: ${props.backgroundColor};` : ""}
      &:has(.action-bar) {
        padding-bottom: 6rem;
      }
    }
    .action-bar {
      position: fixed;
      width: min(40rem, 90vw);
      background-color: var(--white);
      bottom: 0;
      padding: 1rem;
      border-top: 1px solid var(--border-gray);
      z-index: 1;
      animation: 0.3s short-slide-up ease-out;
    }
  }
`;

interface ModalProps {
  title: ReactElement | string;
  onClose: () => void;
  position?: Position;
  minWidth?: string;
  minHeight?: string;
  maxWidth?: string;
  width?: string;
  height?: string;
  className?: string;
  maxHeight?: string;
  padding?: string;
  children?: ReactNode;
  slideFromDirection?: "center" | "right" | "bottom";
  backgroundColor?: string;
  animate?: boolean;
  hideHeaderDecorations?: boolean;
  backgroundBlur?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  title,
  onClose,
  position,
  minWidth,
  minHeight,
  width,
  height,
  className,
  children,
  maxHeight,
  maxWidth,
  padding,
  slideFromDirection = "center",
  backgroundColor,
  animate = true,
  hideHeaderDecorations,
  backgroundBlur,
}) => {
  useKeyPressEvent("Escape", onClose);

  const contentNode = (
    <StyledRoot
      slideFromDirection={slideFromDirection}
      minWidth={minWidth || ""}
      minHeight={minHeight || ""}
      width={width || ""}
      height={height}
      position={position || "center"}
      className={className}
      maxHeight={maxHeight}
      maxWidth={maxWidth}
      padding={padding}
      backgroundColor={backgroundColor}
      onClick={() => {
        onClose();
      }}
      animate={animate}
      backgroundBlur={backgroundBlur}
    >
      <div onClick={(e) => e.stopPropagation()} className="modal">
        {hideHeaderDecorations ? (
          title
        ) : (
          <header>
            <h3>{title}</h3>
            <CloseIcon onClick={onClose} className="close-icon" />
          </header>
        )}
        <div className="body">{children}</div>
      </div>
    </StyledRoot>
  );

  const content = createPortal(
    contentNode,
    document.getElementById("app") as Element,
  );

  return <>{content}</>;
};
